import { useMemo } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import { loyaltyUnitMessages } from 'constants/config/i18nMessages'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'

const InlinePriceRow = styled.span`
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 0.25em;
`

/**
 * Displays a loyalty points burn price, showing how many points the user will spend
 * alongside any remaining cash balance. Handles both standard and LuxPlus member pricing.
 */
interface Props {
  /** The full cash price of the offer before any points are applied */
  totalPrice: number
  /** The number of points the user has chosen to burn */
  pointsToBurn?: number
  /** The full points value of the offer (i.e. the price expressed entirely in points) */
  priceInPoints?: number
  size?: 'S' | 'M' | 'L'
  /** Sale unit label, e.g. "total", "/person", "/transfer" — same values as PriceRowPrice */
  saleUnit?: string
  memberPrice?: number
  /** When true, hides the cash component if it would be zero (points cover the full price) */
  hideZeroCash?: boolean
  /** Points value of the offer at the member cash price (when it differs from priceInPoints) */
  memberPriceInPoints?: number
  /** Show the full offer price as points only (no cash component) */
  pointsOnly?: boolean
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
  mode?: App.LuxPlusPriceStackMode
}

const BURN_POINTS_HEADING_VARIANT: Record<NonNullable<Props['size']>, ComponentProps<typeof Heading>['variant']> = {
  S: 'heading6',
  M: 'heading5',
  L: 'heading4',
}

interface BurnPointsAmountProps {
  points: number
  size: NonNullable<Props['size']>
  colour?: ComponentProps<typeof Heading>['colour']
}

/** Matches PriceRowPointsSpend / PriceRowPrice — number at heading size, unit in small body text */
function BurnPointsAmount({ points, size, colour }: BurnPointsAmountProps) {
  const intl = useIntl()

  return (
    <InlinePriceRow>
      <Heading variant={BURN_POINTS_HEADING_VARIANT[size]} as="span" colour={colour}>
        <FormatDecimal value={points} maximumFractionDigits={0} />
      </Heading>
      <BodyText variant="small" as="span" colour={colour}>
        {intl.formatMessage(loyaltyUnitMessages.pointsAbbreviationPlural)}
      </BodyText>
    </InlinePriceRow>
  )
}

function LuxPlusSaleUnitCaption({ saleUnitLabel }: { saleUnitLabel: ReactNode }) {
  return (
    <Caption variant="medium" as="span" colour="lux-plus" weight="normal" format="lowercase">
      <FormattedMessage
        id="Bqt2LSkQYirkR4WS1QYh"
        defaultMessage="{saleUnit} with {luxPlusLogo}"
        description="Sale unit label with LuxPlus branding for points price"
        values={{
          saleUnit: saleUnitLabel,
          luxPlusLogo: <LuxPlusLogoWrapper height={8} />,
        }}
      />
    </Caption>
  )
}

function LoyaltyBurnPriceDisplay(props: Props) {
  const {
    totalPrice,
    pointsToBurn = 0,
    priceInPoints = 0,
    size = 'L',
    saleUnit = 'total',
    memberPrice,
    hideZeroCash = false,
    memberPriceInPoints,
    pointsOnly = false,
    offerConfig,
    isTour,
    mode,
  } = props

  const intl = useIntl()
  const displayedMode = useLuxPlusDisplayedMode({
    price: totalPrice,
    memberPrice: memberPrice ?? 0,
    offerConfig,
    isTour,
    mode,
  })

  const resolvedMemberPrice = memberPrice && memberPrice > 0 ? memberPrice : totalPrice
  const resolvedMemberPriceInPoints = memberPriceInPoints ?? priceInPoints
  const pointsCashValue = priceInPoints > 0 ? (pointsToBurn * totalPrice) / priceInPoints : 0
  const remainingCash = pointsToBurn >= priceInPoints ? 0 : Math.ceil(Math.max(0, totalPrice - pointsCashValue))
  const memberPointsCashValue = resolvedMemberPriceInPoints > 0
    ? (pointsToBurn * resolvedMemberPrice) / resolvedMemberPriceInPoints
    : 0
  const memberRemainingCash = pointsToBurn >= resolvedMemberPriceInPoints
    ? 0
    : Math.ceil(Math.max(0, resolvedMemberPrice - memberPointsCashValue))
  const effectivePointsBurned = Math.min(pointsToBurn, priceInPoints)
  const memberPointsDisplay = pointsOnly
    ? resolvedMemberPriceInPoints
    : Math.min(pointsToBurn, resolvedMemberPriceInPoints)

  const saleUnitLabel = formatSaleUnitLabel(intl, saleUnit, { withLeadingSlash: true })

  const showCash = hideZeroCash ? remainingCash > 0 : true
  const showMemberCash = hideZeroCash ? memberRemainingCash > 0 : true

  const nonMemberPointsDisplay = pointsOnly ? priceInPoints : effectivePointsBurned

  const nonMemberBurnPriceLabel = useMemo(() => {
    if (pointsOnly || (hideZeroCash && !showCash)) {
      return intl.formatMessage(
        {
          id: 'lRU1m5sm7wOxouX_aYnJ',
          defaultMessage: 'Non-member {points}',
          description: 'Price caption label for non-member points-only display',
        },
        {
          points: <b><FormatLoyaltyPoints points={nonMemberPointsDisplay} abbreviated /></b>,
        },
      )
    }
    return intl.formatMessage(
      {
        id: 'PIwh0lBFZFNGTPKTnNh_',
        defaultMessage: 'Non-member {points} + {price}',
        description: 'Price caption label for non-member points + price display',
      },
      {
        points: <b><FormatLoyaltyPoints points={effectivePointsBurned} abbreviated /></b>,
        price: <b><FormatCurrency value={remainingCash} format="roundedDollar" /></b>,
      },
    )
  }, [intl, effectivePointsBurned, nonMemberPointsDisplay, remainingCash, showCash, hideZeroCash, pointsOnly])

  if (props.pointsToBurn === undefined || props.priceInPoints === undefined) return null

  const headingVariant = BURN_POINTS_HEADING_VARIANT[size]

  const leadWithMemberPrice = displayedMode === 'member-prominent' || displayedMode === 'regular-prominent'
  const isMemberOnly = displayedMode === 'member-only' || displayedMode === 'member-checkout-total' || displayedMode === 'member-compact'

  if (pointsOnly && (leadWithMemberPrice || isMemberOnly)) {
    return (
      <Group direction="vertical" gap={2} data-testid="burn-price-display-luxplus-points-only">
        <InlinePriceRow>
          <BurnPointsAmount points={memberPointsDisplay} size={size} colour="lux-plus" />
          <LuxPlusSaleUnitCaption saleUnitLabel={saleUnitLabel} />
        </InlinePriceRow>
        {leadWithMemberPrice && resolvedMemberPrice < totalPrice && <Caption variant="medium" colour="light-foreground-alt">
          {nonMemberBurnPriceLabel}
        </Caption>}
      </Group>
    )
  }

  if (pointsOnly) {
    return (
      <Group direction="vertical" gap={2}>
        <InlinePriceRow data-testid="burn-price-display-points-only">
          <BurnPointsAmount points={priceInPoints} size={size} colour="normal-contrast" />
          <Caption variant="medium" as="span" colour="light-foreground-alt" weight="normal">
            {saleUnitLabel}
          </Caption>
        </InlinePriceRow>
      </Group>
    )
  }

  if (leadWithMemberPrice || isMemberOnly) {
    return (
      <Group direction="vertical" gap={2} data-testid="burn-price-display-luxplus">
        <InlinePriceRow>
          <BurnPointsAmount points={memberPointsDisplay} size={size} colour="lux-plus" />
          {showMemberCash && (
            <Heading variant={headingVariant} as="span" colour="lux-plus">
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              {' + '}
              <FormatCurrency value={memberRemainingCash} format="roundedDollar" />
            </Heading>
          )}
          <LuxPlusSaleUnitCaption saleUnitLabel={saleUnitLabel} />
        </InlinePriceRow>
        {leadWithMemberPrice && resolvedMemberPrice < totalPrice && <Caption variant="medium" colour="light-foreground-alt">
          {nonMemberBurnPriceLabel}
        </Caption>}
      </Group>
    )
  }

  return (
    <Group direction="vertical" gap={2}>
      <InlinePriceRow data-testid="burn-price-display">
        <BurnPointsAmount points={effectivePointsBurned} size={size} colour="normal-contrast" />
        {showCash && (
          <Heading variant={headingVariant} as="span">
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {' + '}
            <FormatCurrency value={remainingCash} format="roundedDollar" />
          </Heading>
        )}
        <Caption variant="medium" as="span" colour="light-foreground-alt" weight="normal">
          {saleUnitLabel}
        </Caption>
      </InlinePriceRow>
    </Group>
  )
}

export default LoyaltyBurnPriceDisplay
